import { t, useLanguage } from '../i18n';
import React from 'react';
import { View, StyleSheet, Alert, Platform, Text } from 'react-native';
import { Button, Card, useTheme } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';

interface ReviewGateOverlayProps {
  visible: boolean;
  reviewCount: number;
  requiredCount: number;
  onGoToReview: () => void;
  onGoToRecovery: () => void;
  onExempt: () => Promise<void>;
  loading?: boolean;
  bottomOffset?: number;
}

const ReviewGateOverlay: React.FC<ReviewGateOverlayProps> = ({
  visible,
  reviewCount,
  requiredCount,
  onGoToReview,
  onGoToRecovery,
  onExempt,
  loading = false,
  bottomOffset = 0,
}) => {
  useLanguage();
  const theme = useTheme();

  const handleExemptPress = () => {
    const message =
      '今学期は履修していません（休学・テスト用アカウント等）として設定します。\n\nこの設定は学期が変わると自動的にリセットされます。よろしいですか？';

    if (Platform.OS === 'web') {
      if (window.confirm(t(message))) {
        onExempt().catch((err) => {
          console.error('[ReviewGateOverlay] Exempt failed:', err);
          Alert.alert(t("エラー"), t("設定に失敗しました"));
        });
      }
    } else {
      Alert.alert(
        t("履修していない設定"),
        t(message),
        [
          { text: t("キャンセル"), style: 'cancel' },
          {
            text: t("設定する"),
            onPress: () => {
              onExempt().catch((err) => {
                console.error('[ReviewGateOverlay] Exempt failed:', err);
                Alert.alert(t("エラー"), t("設定に失敗しました"));
              });
            },
          },
        ]
      );
    }
  };

  if (!visible) return null;

  return (
    <View style={[styles.backdrop, { bottom: bottomOffset }]}>
      <Card style={[styles.modalContent, { backgroundColor: theme.colors.surface }]}>
        <View style={styles.container}>
          <View style={styles.iconContainer}>
            <Ionicons name="lock-closed" size={64} color={theme.colors.primary} />
          </View>

          <Text style={[styles.title, { color: theme.colors.onSurface }]}>
            {t("レビュー投稿が必要です")}
          </Text>

          <Text style={[styles.description, { color: theme.colors.onSurfaceVariant }]}>
            {t('授業評価の閲覧・履修検索を利用するには、講義レビューを{0}授業以上投稿する必要があります。', [requiredCount])}
          </Text>

          <Card style={styles.progressCard}>
            <Card.Content style={styles.progressContent}>
              <Text style={[styles.progressLabel, { color: theme.colors.onSurfaceVariant }]}>
                {t("投稿進捗")}
              </Text>
              <Text style={[styles.progressCount, { color: theme.colors.primary }]}>
                {reviewCount} / {requiredCount}{t("件")}
              </Text>
            </Card.Content>
          </Card>

          <Button
            mode="contained"
            onPress={onGoToReview}
            style={[styles.primaryButton, { backgroundColor: theme.colors.primary }]}
            labelStyle={styles.buttonLabel}
            disabled={loading}
          >
            {t("レビューを投稿する")}
          </Button>

          <Button
            mode="contained-tonal"
            onPress={onGoToRecovery}
            style={[styles.tertiaryButton, { backgroundColor: theme.colors.secondaryContainer }]}
            labelStyle={styles.buttonLabel}
            disabled={loading}
          >
            {t("時間割を組みなおす")}
          </Button>

          <Button
            mode="outlined"
            onPress={handleExemptPress}
            style={[styles.secondaryButton, { borderColor: theme.colors.outline }]}
            labelStyle={styles.buttonLabel}
            disabled={loading}
          >
            {t("今学期は履修していません")}
          </Button>

          <Text style={[styles.note, { color: theme.colors.onSurfaceVariant }]}>
            {t("※休学・テスト用アカウント等、本来履修していない場合のみ使用してください")}
          </Text>
        </View>
      </Card>
    </View>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    margin: 24,
    borderRadius: 24,
    overflow: 'hidden',
  },
  container: {
    padding: 24,
    alignItems: 'center',
  },
  iconContainer: {
    marginBottom: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 12,
  },
  description: {
    fontSize: 15,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 20,
  },
  progressCard: {
    width: '100%',
    marginBottom: 24,
  },
  progressContent: {
    alignItems: 'center',
    paddingVertical: 12,
  },
  progressLabel: {
    fontSize: 14,
    marginBottom: 4,
  },
  progressCount: {
    fontSize: 28,
    fontWeight: 'bold',
  },
  primaryButton: {
    width: '100%',
    marginBottom: 12,
    borderRadius: 12,
  },
  secondaryButton: {
    width: '100%',
    marginBottom: 16,
    borderRadius: 12,
  },
  tertiaryButton: {
    width: '100%',
    marginBottom: 12,
    borderRadius: 12,
  },
  buttonLabel: {
    fontSize: 16,
    paddingVertical: 4,
  },
  note: {
    fontSize: 12,
    textAlign: 'center',
    lineHeight: 18,
  },
});

export default ReviewGateOverlay;
